import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTranslation } from "react-i18next";
import type { SvgIconComponent } from "@mui/icons-material";
import { AppAutocomplete, AppButton, AppDateTimePicker, AppTextField } from "../form";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import type { DashboardDefaults } from "../../types/dashboard";
import {
  presetFromDashboardDefaults,
  rangeFromDashboardDefaults,
} from "../../utils/dashboardDateDefaults";

export type DateRangePreset = "last7" | "last30" | "thisMonth" | "custom";

export interface DateRangeValue {
  from: string;
  to: string;
}

const IST = "Europe/Istanbul";

/** Converts a valid dayjs to ISO; returns null while the picker value is incomplete/invalid. */
export function toIstanbulIso(d: dayjs.Dayjs | null | undefined): string | null {
  if (!d || !dayjs.isDayjs(d) || !d.isValid()) {
    return null;
  }
  const ms = d.valueOf();
  if (!Number.isFinite(ms)) {
    return null;
  }
  return new Date(ms).toISOString();
}

function requireIstanbulIso(d: dayjs.Dayjs): string {
  const iso = toIstanbulIso(d);
  if (!iso) {
    return new Date().toISOString();
  }
  return iso;
}

export function defaultLast7Days(): DateRangeValue {
  const to = dayjs();
  const from = to.subtract(7, "day");
  return { from: requireIstanbulIso(from), to: requireIstanbulIso(to) };
}

/** Range sent to APIs: custom preset uses picker values; others use committed preset range. */
export const YEAR_LOOKBACK = 2;

export function getSelectableYears(now = dayjs()): number[] {
  const current = now.year();
  return Array.from({ length: YEAR_LOOKBACK + 1 }, (_, index) => current - YEAR_LOOKBACK + index);
}

export type YearMonthChip = { month: number; label: string; disabled: boolean };

export function buildYearMonthChips(
  selectedYear: number,
  language: string,
  now = dayjs()
): YearMonthChip[] {
  const chips: YearMonthChip[] = [];
  for (let month = 0; month < 12; month += 1) {
    const label = new Date(selectedYear, month, 1).toLocaleString(language, { month: "short" });
    const disabled = selectedYear === now.year() && month > now.month();
    chips.push({ month, label, disabled });
  }
  return chips;
}

export function resolveActiveYearMonth(
  activeRange: DateRangeValue,
  selectedYear: number,
  now = dayjs()
): number | null {
  const from = dayjs(activeRange.from);
  const to = dayjs(activeRange.to);
  if (from.year() !== selectedYear || to.year() !== selectedYear) {
    return null;
  }
  const month = from.month();
  if (from.month() !== to.month()) {
    return null;
  }
  if (!from.isSame(from.startOf("month"), "day")) {
    return null;
  }
  if (selectedYear === now.year() && month > now.month()) {
    return null;
  }
  return month;
}

export function rangeForYearMonth(year: number, month: number, now = dayjs()): DateRangeValue {
  const start = dayjs().year(year).month(month).startOf("month");
  const end = year === now.year() && month === now.month() ? now : dayjs().year(year).month(month).endOf("month");
  return { from: requireIstanbulIso(start), to: requireIstanbulIso(end) };
}

export function getActiveDateRange(
  preset: DateRangePreset,
  range: DateRangeValue,
  customFrom: dayjs.Dayjs,
  customTo: dayjs.Dayjs
): DateRangeValue {
  if (preset === "custom") {
    const from = toIstanbulIso(customFrom);
    const to = toIstanbulIso(customTo);
    // Keep last committed range while the user is mid-typing an incomplete date.
    if (from && to) {
      return { from, to };
    }
    return range;
  }
  return range;
}

export function useDateRangeHeader(initial?: DateRangeValue, initialPreset?: DateRangePreset) {
  const { i18n } = useTranslation();
  const [range, setRange] = useState<DateRangeValue>(initial || defaultLast7Days());
  const [preset, setPreset] = useState<DateRangePreset>(initialPreset || "last7");
  const [customFrom, setCustomFrom] = useState(dayjs(range.from));
  const [customTo, setCustomTo] = useState(dayjs(range.to));
  const [selectedYear, setSelectedYear] = useState(() => dayjs().year());

  const availableYears = useMemo(() => getSelectableYears(), []);

  const applyPreset = (p: DateRangePreset) => {
    setPreset(p);
    const now = dayjs();
    if (p === "last7") {
      setRange({ from: requireIstanbulIso(now.subtract(7, "day")), to: requireIstanbulIso(now) });
    } else if (p === "last30") {
      setRange({ from: requireIstanbulIso(now.subtract(30, "day")), to: requireIstanbulIso(now) });
    } else if (p === "thisMonth") {
      setRange({ from: requireIstanbulIso(now.startOf("month")), to: requireIstanbulIso(now) });
    } else if (p === "custom") {
      setCustomFrom(dayjs(range.from));
      setCustomTo(dayjs(range.to));
    }
  };

  const applyCustom = () => {
    const from = toIstanbulIso(customFrom);
    const to = toIstanbulIso(customTo);
    if (!from || !to) {
      return;
    }
    setRange({ from, to });
    setPreset("custom");
  };

  const applyYearMonth = useCallback(
    (month: number) => {
      const next = rangeForYearMonth(selectedYear, month);
      setCustomFrom(dayjs(next.from));
      setCustomTo(dayjs(next.to));
      setRange(next);
      setPreset("custom");
    },
    [selectedYear]
  );

  const applyYear = useCallback((year: number) => {
    setSelectedYear(year);
  }, []);

  const yearMonthChips = useMemo(
    () => buildYearMonthChips(selectedYear, i18n.language),
    [i18n.language, selectedYear]
  );

  const activeRange = useMemo(
    () => getActiveDateRange(preset, range, customFrom, customTo),
    [preset, range, customFrom, customTo]
  );

  useEffect(() => {
    if (preset !== "custom") return;
    const from = toIstanbulIso(customFrom);
    const to = toIstanbulIso(customTo);
    if (!from || !to) return;
    setRange({ from, to });
  }, [preset, customFrom, customTo]);

  useEffect(() => {
    const from = dayjs(activeRange.from);
    const to = dayjs(activeRange.to);
    if (from.year() === to.year() && availableYears.includes(from.year())) {
      setSelectedYear(from.year());
    }
  }, [activeRange.from, activeRange.to, availableYears]);

  const activeYearMonth = useMemo(
    () => resolveActiveYearMonth(activeRange, selectedYear),
    [activeRange, selectedYear]
  );

  const applyDashboardDefaults = (defaults?: DashboardDefaults) => {
    const nextPreset = presetFromDashboardDefaults(defaults);
    const nextRange = rangeFromDashboardDefaults(defaults);
    setPreset(nextPreset);
    setRange(nextRange);
    setCustomFrom(dayjs(nextRange.from));
    setCustomTo(dayjs(nextRange.to));
  };

  const label = useMemo(() => {
    const fmt = (iso: string) => {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleString("tr-TR", { timeZone: IST, dateStyle: "short", timeStyle: "short" });
    };
    return `${fmt(activeRange.from)} — ${fmt(activeRange.to)}`;
  }, [activeRange]);

  return {
    range: activeRange,
    activeRange,
    preset,
    setPreset: applyPreset,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    applyCustom,
    applyDashboardDefaults,
    applyYearMonth,
    applyYear,
    selectedYear,
    availableYears,
    yearMonthChips,
    activeYearMonth,
    label,
  };
}

const PRESET_OPTIONS: DateRangePreset[] = ["last7", "last30", "thisMonth", "custom"];

type PresetOption = {
  value: DateRangePreset;
  label: string;
  icon: SvgIconComponent;
};

function presetIcon(p: DateRangePreset) {
  switch (p) {
    case "last7":
    case "last30":
      return DateRangeOutlinedIcon;
    case "thisMonth":
      return CalendarTodayOutlinedIcon;
    default:
      return TuneOutlinedIcon;
  }
}

function MonthTile({
  label,
  active,
  disabled = false,
  onClick,
  ariaLabel,
  compact = false,
}: {
  label: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  ariaLabel: string;
  compact?: boolean;
}) {
  const custom = useAppCustomStyles();
  const styles = custom.components.dateRangeHeader;

  return (
    <AppButton
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={active}
      variant="outlined"
      color={active ? "primary" : "inherit"}
      sx={styles.monthTile(active, compact, disabled)}
    >
      <CalendarMonthOutlinedIcon sx={styles.monthTileIcon(active)} />
      <Typography
        variant="body2"
        fontWeight={active ? 800 : 600}
        sx={styles.monthTileLabel}
      >
        {label}
      </Typography>
    </AppButton>
  );
}

type Props = {
  range: DateRangeValue;
  preset: DateRangePreset;
  onPresetChange: (p: DateRangePreset) => void;
  customFrom: dayjs.Dayjs;
  customTo: dayjs.Dayjs;
  onCustomFromChange: (d: dayjs.Dayjs) => void;
  onCustomToChange: (d: dayjs.Dayjs) => void;
  onApplyCustom: () => void;
  label: string;
  onRefresh?: () => void;
  executing?: boolean;
  showYearMonthChips?: boolean;
  selectedYear?: number;
  availableYears?: number[];
  onYearSelect?: (year: number) => void;
  yearMonthChips?: YearMonthChip[];
  activeYearMonth?: number | null;
  onYearMonthSelect?: (month: number) => void;
  /** Vertical stack for share sidebar and narrow panels. */
  layout?: "horizontal" | "vertical";
};

export default function DateRangeHeader(props: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.dateRangeHeader;
  const {
    preset,
    onPresetChange,
    customFrom,
    customTo,
    onCustomFromChange,
    onCustomToChange,
    onApplyCustom,
    label,
    onRefresh,
    executing = false,
    showYearMonthChips,
    selectedYear = new Date().getFullYear(),
    availableYears = [],
    onYearSelect,
    yearMonthChips = [],
    activeYearMonth = null,
    onYearMonthSelect,
    layout = "horizontal",
  } = props;

  const isVertical = layout === "vertical";

  const presetLabel = (p: DateRangePreset) => {
    switch (p) {
      case "last7":
        return t("dateRange.last7days");
      case "last30":
        return t("dateRange.last30days");
      case "thisMonth":
        return t("dateRange.thisMonth");
      default:
        return t("dateRange.custom");
    }
  };

  const presetOptions: PresetOption[] = useMemo(
    () =>
      PRESET_OPTIONS.map((p) => ({
        value: p,
        label: presetLabel(p),
        icon: presetIcon(p),
      })),
    [t]
  );

  const selectedPreset =
    presetOptions.find((o) => o.value === preset) ?? presetOptions[0];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper elevation={0} sx={styles.paper}>
        <Stack
          direction={isVertical ? "column" : { xs: "column", lg: "row" }}
          alignItems={isVertical ? "stretch" : { xs: "stretch", lg: "center" }}
          justifyContent="space-between"
          spacing={1.5}
          sx={styles.headerStack(isVertical)}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={styles.filtersLabelStack}>
            <DateRangeOutlinedIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={700} noWrap>
              {t("dashboard.filters")}
            </Typography>
            {executing && (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {t("dashboard.executing")}
                </Typography>
              </Stack>
            )}
          </Stack>
          <Stack
            direction={isVertical ? "column" : { xs: "column", sm: "row" }}
            spacing={1}
            alignItems="stretch"
            sx={styles.actionsStack(isVertical)}
          >
            <Chip
              size="small"
              variant="outlined"
              icon={<CalendarMonthOutlinedIcon fontSize="small" />}
              label={label}
              sx={styles.rangeChip(isVertical)}
            />
            {onRefresh && (
              <AppButton
                variant="contained"
                size="small"
                disabled={executing}
                startIcon={<RefreshOutlinedIcon />}
                onClick={onRefresh}
                fullWidth={isVertical}
                sx={styles.refreshButton}
              >
                {t("common.refresh")}
              </AppButton>
            )}
          </Stack>
        </Stack>

        <Box sx={styles.body(isVertical)}>
          <Box sx={styles.filtersGrid(isVertical)}>
            <Box sx={styles.presetField(isVertical)}>
              <AppAutocomplete<PresetOption>
                label={t("dateRange.label")}
                options={presetOptions}
                value={selectedPreset}
                onChange={(_, option) => option && onPresetChange(option.value)}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                disableClearable
                size="small"
                sx={styles.presetAutocomplete}
                slotProps={{
                  paper: {
                    sx: styles.presetAutocompletePaper,
                  },
                }}
                renderOption={(props, option) => {
                  const Icon = option.icon;
                  const { key, ...rest } = props as typeof props & { key: string };
                  return (
                    <ListItem key={key} {...rest} sx={styles.presetListOption}>
                      <ListItemIcon sx={styles.presetListIcon}>
                        <Icon fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText primary={option.label} primaryTypographyProps={{ fontWeight: 500 }} />
                    </ListItem>
                  );
                }}
                renderInput={(params) => (
                  <AppTextField
                    {...params}
                    label={t("dateRange.label")}
                    size="small"
                    margin="dense"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <EventOutlinedIcon
                            fontSize="small"
                            color="action"
                            sx={styles.presetInputIcon}
                          />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {preset === "custom" && (
              <>
                <Box sx={styles.customFromField}>
                  <AppDateTimePicker
                    label={t("dateRange.from")}
                    value={customFrom}
                    onChange={(v) => {
                      // Keep incomplete keyboard values in state; ISO sync skips invalids.
                      if (v) onCustomFromChange(v);
                    }}
                    onAccept={(v) => {
                      if (v && v.isValid()) onCustomFromChange(v);
                    }}
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                  />
                </Box>
                <Box sx={styles.customToField}>
                  <AppDateTimePicker
                    label={t("dateRange.to")}
                    value={customTo}
                    onChange={(v) => {
                      if (v) onCustomToChange(v);
                    }}
                    onAccept={(v) => {
                      if (v && v.isValid()) onCustomToChange(v);
                    }}
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                  />
                </Box>
                <Box sx={styles.customApplyField(isVertical)}>
                  <AppButton
                    variant="outlined"
                    size="small"
                    onClick={onApplyCustom}
                    disabled={!customFrom?.isValid?.() || !customTo?.isValid?.()}
                    fullWidth={isVertical}
                    sx={styles.customApplyButton(isVertical)}
                  >
                    {t("dateRange.apply")}
                  </AppButton>
                </Box>
              </>
            )}
          </Box>

          {showYearMonthChips && yearMonthChips.length > 0 && (
            <>
              <Divider sx={styles.divider} />
              {availableYears.length > 0 && (
                <Stack direction="row" spacing={0.75} sx={styles.yearRow}>
                  {availableYears.map((year) => (
                    <AppButton
                      key={year}
                      type="button"
                      size="small"
                      variant={selectedYear === year ? "contained" : "outlined"}
                      color={selectedYear === year ? "primary" : "inherit"}
                      onClick={() => onYearSelect?.(year)}
                      aria-pressed={selectedYear === year}
                      sx={styles.yearButton(selectedYear === year)}
                    >
                      {year}
                    </AppButton>
                  ))}
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={styles.yearMonthsLabel}>
                {t("dateRange.yearMonths", { year: selectedYear })}
              </Typography>
              <Box sx={styles.monthGrid()}>
                {yearMonthChips.map(({ month, label: monthLabel, disabled }) => (
                  <MonthTile
                    key={month}
                    compact={isVertical}
                    label={monthLabel}
                    active={activeYearMonth === month}
                    disabled={disabled}
                    onClick={() => onYearMonthSelect?.(month)}
                    ariaLabel={t("dateRange.selectMonth", { month: monthLabel, year: selectedYear })}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </LocalizationProvider>
  );
}
